import { useTranslation } from "react-i18next";
import type { Integrator } from "../../../../../../store/reseller-admin/integratorsStore";
import type { ThemeClasses } from "../types";
import IntegratorGridCard from "./IntegratorGridCard";
import IntegratorListRow from "./IntegratorListRow";

interface IntegratorListProps {
  integrators: Integrator[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  selectedView: "grid" | "list";
  onDetails: (integrator: Integrator) => void;
  onEdit: (integrator: Integrator) => void;
  onDelete: (integrator: Integrator) => void;
  editingId?: string | null;
}

export default function IntegratorList({
  integrators,
  themeClasses,
  isDarkMode,
  selectedView,
  onDetails,
  onEdit,
  onDelete,
  editingId,
}: IntegratorListProps) {
  const { t } = useTranslation("resellerAdmin");

  if (integrators.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <i className={`ri-plug-line text-5xl ${themeClasses.textSecondary} mb-4`}></i>
        <h3 className={`text-lg font-semibold ${themeClasses.text} mb-2`}>
          {t("integrators.noIntegrators")}
        </h3>
        <p className={`text-sm ${themeClasses.textSecondary}`}>
          {t("integrators.noIntegratorsDescription")}
        </p>
      </div>
    );
  }

  if (selectedView === "grid") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {integrators.map((integrator) => (
          <IntegratorGridCard
            key={integrator.id}
            integrator={integrator}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onDetails={onDetails}
            onEdit={onEdit}
            onDelete={onDelete}
            editLoading={editingId === integrator.id}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {integrators.map((integrator) => (
        <IntegratorListRow
          key={integrator.id}
          integrator={integrator}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onDetails={onDetails}
          onEdit={onEdit}
          onDelete={onDelete}
          editLoading={editingId === integrator.id}
        />
      ))}
    </div>
  );
}
